import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  FileText, Phone, Users, Mail, Presentation, 
  Plus, Pencil, Trash2, MessageSquare, Loader2, CalendarIcon 
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { OpportunityNote, NOTE_TYPES, useOpportunityNotes } from '@/hooks/useOpportunities';
import { useAuth } from '@/hooks/useAuth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface OpportunityNotesProps {
  opportunityId: string;
}

const NOTE_ICONS: Record<string, React.ElementType> = {
  notitie: FileText,
  telefoongesprek: Phone,
  meeting: Users,
  email: Mail,
  presentatie: Presentation,
};

export function OpportunityNotes({ opportunityId }: OpportunityNotesProps) {
  const { notes, loading, addNote, updateNote, deleteNote } = useOpportunityNotes(opportunityId);
  const { user, userRole } = useAuth();
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('notitie');
  const [noteDate, setNoteDate] = useState<Date>(new Date());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    await addNote(newNote.trim(), noteType, noteDate);
    setNewNote('');
    setNoteType('notitie');
    setNoteDate(new Date());
    setSaving(false);
  };

  const handleUpdateNote = async () => {
    if (!editingId || !editText.trim()) return;
    setSaving(true);
    await updateNote(editingId, editText.trim());
    setEditingId(null);
    setEditText('');
    setSaving(false);
  };

  const handleDeleteNote = async () => {
    if (!deleteId) return;
    await deleteNote(deleteId);
    setDeleteId(null);
  };

  const canEditNote = (note: OpportunityNote) => {
    return note.created_by === user?.id || 
           userRole === 'admin' || 
           userRole === 'super_admin';
  };

  const startEdit = (note: OpportunityNote) => {
    setEditingId(note.id);
    setEditText(note.body);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Gespreksverslagen
        </CardTitle>
        <Badge variant="secondary">{notes.length}</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* New note form */}
        <div className="space-y-3 p-4 bg-muted/50 rounded-lg border">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={noteType} onValueChange={setNoteType}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NOTE_TYPES.map((type) => {
                  const Icon = NOTE_ICONS[type.id];
                  return (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {format(noteDate, 'd MMM yyyy', { locale: nl })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={noteDate}
                  onSelect={(date) => date && setNoteDate(date)}
                  locale={nl}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Schrijf een notitie..."
            rows={3}
          />
          <Button 
            onClick={handleAddNote} 
            disabled={!newNote.trim() || saving}
            className="w-full sm:w-auto"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Notitie toevoegen
          </Button>
        </div>

        {/* Notes list */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nog geen gespreksverslagen
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map((note) => {
              const Icon = NOTE_ICONS[note.note_type] || FileText;
              const noteTypeLabel = NOTE_TYPES.find(t => t.id === note.note_type)?.label || 'Notitie';
              const isEditing = editingId === note.id;
              const displayDate = note.note_date || note.created_at;

              return (
                <div 
                  key={note.id} 
                  className="border rounded-lg p-4 bg-card"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant="outline" className="text-xs">
                            {noteTypeLabel}
                          </Badge>
                          <span className="text-xs text-muted-foreground font-medium">
                            {format(new Date(displayDate), "d MMMM yyyy", { locale: nl })}
                          </span>
                          {/*
                            `note.profile` is `null` zodra RLS de profielrij van
                            de auteur wegfiltert — een to-one embed zonder
                            `!inner` geeft dan stil null terug, zonder fout. Na
                            migratie 062 gebeurt dat voor een charging_user bij
                            elke notitie van een ander. Zonder terugval verdween
                            de hele regel en leek het scherm kapot; nu staat er
                            dat de auteur bekend is maar niet zichtbaar.
                          */}
                          <span className="text-xs text-muted-foreground">
                            • {note.profile
                                ? (note.profile.display_name || note.profile.email)
                                : 'een collega'}
                          </span>
                        </div>
                        
                        {isEditing ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              rows={3}
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={handleUpdateNote} disabled={saving}>
                                {saving ? 'Opslaan...' : 'Opslaan'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                                Annuleren
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{note.body}</p>
                        )}
                      </div>
                    </div>

                    {canEditNote(note) && !isEditing && (
                      <div className="flex items-center gap-1">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8"
                          onClick={() => startEdit(note)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(note.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Notitie verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze notitie wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteNote} className="bg-destructive text-destructive-foreground">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
